export type TopicStatsSortMode = 'display' | 'views' | 'redemptions';

type TopicWithStats = {
  view_count: number;
  redeem_count: number;
};

export function sortAdminTopicsByMetric<T extends TopicWithStats>(
  topics: readonly T[],
  mode: TopicStatsSortMode,
): T[] {
  if (mode === 'display') return [...topics];

  const field = mode === 'views' ? 'view_count' : 'redeem_count';
  return [...topics].sort((left, right) => right[field] - left[field]);
}

// ---- 素材完整性与筛选 ----

/** 前台详情页依赖的预览素材；文稿预览图是可选项，不计入“缺失” */
const REQUIRED_PREVIEW_FIELDS = [
  ['cover_image', '封面'],
  ['video_display', '视频'],
  ['ppt_display', 'PPT预览图'],
] as const;

type TopicAssetFields = {
  is_active: boolean;
  content_category: string;
  cover_image: string | null;
  video_display: string | null;
  ppt_display: string | null;
  download_link: string | null;
};

export type TopicIssueFilter = 'all' | 'missing-preview' | 'missing-resource';

/** 缺少的必需预览素材名称，例如 ['视频', 'PPT预览图'] */
export function getMissingPreviewLabels(topic: TopicAssetFields): string[] {
  return REQUIRED_PREVIEW_FIELDS.filter(([field]) => !topic[field]?.trim()).map(([, label]) => label);
}

export function isMissingResource(topic: TopicAssetFields): boolean {
  return !topic.download_link?.trim();
}

export interface TopicAdminSummary {
  active: number;
  inactive: number;
  missingPreview: number;
  missingResource: number;
}

/** 统计卡数据；素材和领取资源只统计已上架主题，因为只有它们会被家长看到 */
export function summarizeAdminTopics(topics: readonly TopicAssetFields[]): TopicAdminSummary {
  const summary: TopicAdminSummary = { active: 0, inactive: 0, missingPreview: 0, missingResource: 0 };
  for (const topic of topics) {
    if (!topic.is_active) {
      summary.inactive += 1;
      continue;
    }
    summary.active += 1;
    if (getMissingPreviewLabels(topic).length > 0) summary.missingPreview += 1;
    if (isMissingResource(topic)) summary.missingResource += 1;
  }
  return summary;
}

export function filterAdminTopics<T extends TopicAssetFields>(
  topics: readonly T[],
  options: { category: string; issue: TopicIssueFilter },
): T[] {
  return topics.filter((topic) => {
    if (options.category && topic.content_category !== options.category) return false;
    if (options.issue === 'missing-preview') return getMissingPreviewLabels(topic).length > 0;
    if (options.issue === 'missing-resource') return isMissingResource(topic);
    return true;
  });
}
